import { useMemo } from "react";
import {
  Control,
  Controller,
  FieldPathValue,
  FieldValues,
  Path,
} from "react-hook-form";
import AITiptapEditor from "./TiptapEditor/AITiptapEditor";
import { NoxusFormLabel } from "./NoxusFormLabel";
import {
  AdvancedChipOption,
  hasChipValues,
} from "./TiptapEditor/chipOptionsConfig";
import { RichTextValue, getTemplateStringValue } from "./RichTextField";
import {
  appendNodeConfigContext,
  useNodeConfigContext,
} from "@/hooks/useNodeConfigContext";

interface IRichTextFieldAI<TFieldValues extends FieldValues = FieldValues> {
  options?: AdvancedChipOption[];
  control: Control<TFieldValues>;
  defaultValue?: FieldPathValue<TFieldValues, Path<TFieldValues>>;
  name: Path<TFieldValues>;
  label?: string;
  tooltip?: string;
  placeholder?: string;
  unselectAllNodes?: () => void;
  isControlled?: boolean;
  isDisabled?: boolean;
  improverPrompt?: string;
  enableHeuristics?: boolean;
  heuristicContext?: string;
  heuristicTypes?: string[];
  improverContext?: string;
}

export const RichTextFieldAI = <TFieldValues extends FieldValues>(
  props: IRichTextFieldAI<TFieldValues>
) => {
  const nodeConfigContext = useNodeConfigContext(props.control, props.name);
  const heuristicContext = useMemo(
    () => appendNodeConfigContext(props.heuristicContext, nodeConfigContext),
    [nodeConfigContext, props.heuristicContext]
  );
  const improverContext = useMemo(
    () => appendNodeConfigContext(props.improverContext, nodeConfigContext),
    [nodeConfigContext, props.improverContext]
  );

  return (
    <Controller
      control={props.control}
      name={props.name}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value, onBlur } }) => {
        const handleOnChange = (newValue: RichTextValue[]) => {
          onChange(newValue);
        };

        // Because of a bug we had, there may be the case where this value is not an array. If it is, we need to make it an array.
        // Added on March 25, 2025. Can be safely removed a while after that date.
        const arrayedValue = Array.isArray(value) ? value : [value];
        const parsedValue = arrayedValue?.map((v: RichTextValue | string) => {
          return typeof v === "string" ||
            (typeof v === "object" &&
              v !== null &&
              "text" in v &&
              !("definition" in v))
            ? getTemplateStringValue(v)
            : v;
        });

        return (
          <div className="w-full">
            {props.label && (
              <NoxusFormLabel
                label={props.label}
                tooltip={props.tooltip}
                name={props.name}
                isDisabled={props.isDisabled}
              />
            )}

            <AITiptapEditor
              onChange={handleOnChange}
              value={parsedValue}
              label={props.label}
              chipOptions={props.options}
              unselectAllNodes={props.unselectAllNodes}
              placeholder={props.placeholder}
              isControlled={props.isControlled}
              isDisabled={props.isDisabled}
              improverPrompt={props.improverPrompt}
              enableHeuristics={props.enableHeuristics}
              heuristicContext={heuristicContext}
              heuristicTypes={props.heuristicTypes}
              improverContext={improverContext}
            />
            {hasChipValues(props.options) && (
              <span className="text-xs font-regular leading-normal text-muted-foreground mt-1.5 whitespace-pre-line">
                Insert{" "}
                <span className="font-semibold text-foreground bg-muted rounded-sm px-1.5">
                  /
                </span>{" "}
                for a variable value.
              </span>
            )}
          </div>
        );
      }}
    />
  );
};
